import { useEffect, useMemo, useRef, useState } from "react";
import { AssistantMessageBlock } from "../types/assistant";
import { exhaustivenessCheck } from "../utils/misc";

type Step = { next: [number, number]; delay: number };

type SegmentedBlock =
  | Extract<AssistantMessageBlock, { type: "photos" }>
  | (Extract<AssistantMessageBlock, { type: "paragraph" }> & {
      stepCount: number;
    })
  | (Extract<AssistantMessageBlock, { type: "markdown" }> & {
      stepCount: number;
      charPositions: number[];
    });

type AnimationOptions = {
  disableAnimation?: boolean;
  charInterval?: number;
  photoDuration?: number;
  onComplete?: () => void;
  onUpdate?: () => void;
};

const MARKDOWN_SKIP_PATTERNS: RegExp[] = [
  /!\[[^\]]*\]\([^)]*\)/g,                      // ![alt](url) — images
  /```[\s\S]*?```/g,                             // ```...``` — fenced code blocks
  // /\*\*\*(?:[^*]|\*(?!\*\*))+\*\*\*/g,          // ***bold italic***
  // /\*\*(?:[^*]|\*(?!\*))+\*\*/g,                // **bold**
  // /(?<!\*)\*(?!\*)(?:[^*\n])+\*(?!\*)/g,        // *italic*
  /\[[^\]]*\]\([^)]*\)/g,                        // [text](url) — links
  /(?:^\|[^\n]*\|[ \t]*(?:\n|$))+/gm,           // | col | col | — tables
  // /^#{1,6}\s[^\n]*/gm,                           // # heading — headers
];

const computeSkipRanges = (content: string): Array<[number, number]> => {
  const ranges: Array<[number, number]> = [];

  for (const pattern of MARKDOWN_SKIP_PATTERNS) {
    pattern.lastIndex = 0;
    let match;
    while ((match = pattern.exec(content)) !== null) {
      ranges.push([match.index, match.index + match[0].length]);
    }
  }

  ranges.sort((a, b) => a[0] - b[0]);

  const merged: Array<[number, number]> = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) {
      last[1] = Math.max(last[1], range[1]);
    } else {
      merged.push([range[0], range[1]]);
    }
  }

  return merged;
};

const computeCharPositions = (content: string): number[] => {
  const skipRanges = computeSkipRanges(content);
  const positions: number[] = [];
  let i = 0;

  while (i < content.length) {
    const skipRange = skipRanges.find(([start]) => start === i);
    if (skipRange) {
      i = skipRange[1];
    } else {
      i++;
    }
    positions.push(i);
  }

  return positions;
};

export const useMessageBlocksAnimation = (
  blocks: AssistantMessageBlock[],
  options: AnimationOptions,
) => {
  const [indexes, setIndexes] = useState<[number, number]>([0, 0]);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(options.onComplete);
  const onUpdateRef = useRef(options.onUpdate);
  onCompleteRef.current = options.onComplete;
  onUpdateRef.current = options.onUpdate;

  const segmentedBlocks = useMemo((): SegmentedBlock[] => {
    return blocks.map((block) => {
      switch (block.type) {
        case "photos":
          return block;
        case "paragraph":
          return { ...block, stepCount: block.text.length };
        case "markdown": {
          const charPositions = computeCharPositions(block.content);
          return { ...block, stepCount: charPositions.length, charPositions };
        }
        default:
          return exhaustivenessCheck(block);
      }
    });
  }, [blocks]);

  const iterate = useMemo(() => {
    const charInterval = options.charInterval ?? 12;
    const photoDuration = options.photoDuration ?? charInterval;

    return function* (): Generator<Step, void, unknown> {
      for (let i = 0; i < segmentedBlocks.length; i++) {
        const cur = segmentedBlocks[i];
        switch (cur.type) {
          case "photos":
            yield { next: [i + 1, 0], delay: photoDuration };
            break;

          case "paragraph":
          case "markdown":
            for (let j = 0; j < cur.stepCount; j++) {
              yield { next: [i, j + 1], delay: charInterval };
            }
            yield { next: [i + 1, 0], delay: 0 };
            break;

          default:
            exhaustivenessCheck(cur);
        }
      }
    };
  }, [segmentedBlocks, options.charInterval, options.photoDuration]);

  useEffect(() => {
    if (options.disableAnimation) {
      setIndexes([segmentedBlocks.length, 0]);
      onUpdateRef.current?.();
      if (!completedRef.current) {
        completedRef.current = true;
        onCompleteRef.current?.();
      }
      return;
    }

    const gen = iterate();
    completedRef.current = false;
    setIndexes([0, 0]);

    let timer: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const advance = () => {
      const step = gen.next();
      if (!step || step.done) {
        if (!completedRef.current) {
          completedRef.current = true;
          onCompleteRef.current?.();
        }
        return;
      }
      timer = setTimeout(() => {
        if (cancelled) return;
        setIndexes(step.value.next);
        advance();
      }, step.value.delay);
    };

    advance();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [iterate, options.disableAnimation, segmentedBlocks.length]);

  useEffect(() => {
    if (options.disableAnimation) {
      return;
    }
    onUpdateRef.current?.();
  }, [indexes, options.disableAnimation]);

  return {
    segmentedBlocks,
    blockIndex: indexes[0],
    stepIndex: indexes[1],
  };
};
